import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStickyNote } from '@fortawesome/free-solid-svg-icons';
import styles from './DescriptionEditor.module.css';
import { useTranslation } from 'react-i18next';

interface DescriptionEditorProps {
  description: string;
  onSave: (newDescription: string) => void;
  disabled?: boolean;
}

const DescriptionEditor: React.FC<DescriptionEditorProps> = ({
  description,
  onSave,
  disabled = false
}) => {
  const { t } = useTranslation('videoEditor');
  const [isOpen, setIsOpen] = useState(false);
  const [localDescription, setLocalDescription] = useState(description);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Update local state when prop changes
  useEffect(() => {
    setLocalDescription(description);
  }, [description]);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        handleSave();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, localDescription, description]); // Add dependencies

  const handleSave = () => {
    console.log('🔍 handleSave called:', { localDescription, description, changed: localDescription !== description });
    if (localDescription !== description) {
      console.log('💾 Saving description:', localDescription);
      onSave(localDescription);
    }
    setIsOpen(false);
  };

  const handleBlur = () => {
    handleSave();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      setLocalDescription(description); // Reset to original
      setIsOpen(false);
    }
    // Note: Don't close on Enter - allow multi-line descriptions
  };

  return (
    <div className={styles.descriptionEditorContainer}>
      <button
        ref={buttonRef}
        className={styles.editButton}
        onClick={() => setIsOpen(!isOpen)}
        title={t('videoEditor.description.editDescription')}
        disabled={disabled}
      >
        <FontAwesomeIcon style={{ fontSize: "14px" }} icon={faStickyNote} />
      </button>

      {isOpen && (
        <div ref={popoverRef} className={styles.popover}>
          <div className={styles.popoverHeader}>
            <span className={styles.popoverTitle}>{t('videoEditor.description.title')}</span>
          </div>
          <textarea
            className={styles.descriptionTextarea}
            value={localDescription}
            onChange={(e) => setLocalDescription(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={t('videoEditor.description.placeholder')}
            rows={4}
            autoFocus
          />
          <div className={styles.popoverFooter}>
            <span className={styles.hint}>{t('videoEditor.description.cancelHint')}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DescriptionEditor;
